import React, { useMemo, ReactNode } from 'react';
import { useRBACDistricts, useRBACSchools, useRBACDefaults } from '@/hooks/rbac/useRBACOrganizationQueries';
import { useRBACStore } from '@/stores/rbac-store';
import useOrgDataStore from '@/stores/org-store';
import { Org } from '@/types/dynamodb-tables';

/**
 * Enhanced RBAC Data Context Provider
 * 
 * This context handles all data fetching and state management for the RBAC system.
 * It's split from the permissions logic (rbac-permissions-context.tsx) for:
 * - Better performance (prevents unnecessary re-renders when data changes)
 * - Separation of concerns (data vs. permission logic)
 * - Improved caching and invalidation strategies
 * 
 * Features:
 * - Fetches districts and schools based on user role
 * - Manages organization selection state
 * - Provides refetch functions for cache invalidation
 * - Handles loading states and errors
 */

// Data context interface for districts, schools, and selection state
export interface RBACDataContextValue {
  districts: Org[];
  schools: Org[];
  isLoadingDistricts: boolean;
  isLoadingSchools: boolean;
  selectedDistrict: Org | null;
  setSelectedDistrict: (district: Org) => void;
  clearSelectedDistrict: () => void;
  selectedSchool: Org | null;
  setSelectedSchool: (school: Org) => void;
  clearSelectedSchool: () => void;
  schoolSelectorWarning: boolean;
  setSchoolSelectorWarning: (warning: boolean) => void;
  defaultDistrict: Org | null;
  defaultSchool: Org | null;
  refetchDistricts: () => void;
  refetchSchools: () => void;
}

const RBACDataContext = React.createContext<RBACDataContextValue | null>(null);

// Export context for use in hook
export { RBACDataContext };

interface RBACDataProviderProps {
  children: ReactNode;
}

/**
 * RBACDataProvider handles all data fetching and state management
 * Separated from permissions logic for better performance
 */
export const RBACDataProvider = ({ children }: RBACDataProviderProps) => {
  // Organization selection state from org store
  const { 
    selectedDistrict, 
    setSelectedDistrict, 
    selectedSchool, 
    setSelectedSchool,
    clearSelectedDistrict,
    clearSelectedSchool,
    schoolSelectorWarning,
    setSchoolSelectorWarning,
    refetchTrigger
  } = useOrgDataStore();

  const { getUserRole, initializeRBAC } = useRBACStore();
  const userRole = getUserRole();

  // Ensure initializeRBAC is only called once per session
  const hasInitialized = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitialized.current) {
      initializeRBAC();
      hasInitialized.current = true;
    }
  }, [initializeRBAC]);

  // Fetch all accessible districts for the user
  const { data: districts = [], isLoading: isLoadingDistricts, refetch: refetchDistricts } = useRBACDistricts({ 
    enabled: !!userRole,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });
  
  // Refetch districts when refetchTrigger changes
  React.useEffect(() => {
    if (refetchTrigger > 0) {
      refetchDistricts();
    }
  }, [refetchTrigger, refetchDistricts]);

  // Fetch all accessible schools for the user (based on selected district)
  const { data: schools = [], isLoading: isLoadingSchools, refetch: refetchSchools } = useRBACSchools(
    selectedDistrict?.orgId || undefined,
    { 
      enabled: !!userRole && (!!selectedDistrict?.orgId || userRole === 'school_champion'),
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  );
  
  // Refetch schools when refetchTrigger changes
  React.useEffect(() => {
    if (refetchTrigger > 0) {
      refetchSchools();
    }
  }, [refetchTrigger, refetchSchools]);

  // Get default district and school for the user
  const { defaultDistrict, defaultSchool } = useRBACDefaults();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<RBACDataContextValue>(() => ({
    districts,
    schools,
    isLoadingDistricts,
    isLoadingSchools,
    selectedDistrict,
    setSelectedDistrict,
    clearSelectedDistrict,
    selectedSchool,
    setSelectedSchool,
    clearSelectedSchool,
    schoolSelectorWarning,
    setSchoolSelectorWarning,
    defaultDistrict: defaultDistrict || null,
    defaultSchool: defaultSchool || null,
    refetchDistricts,
    refetchSchools,
  }), [
    districts,
    schools,
    isLoadingDistricts,
    isLoadingSchools,
    selectedDistrict,
    setSelectedDistrict,
    clearSelectedDistrict,
    selectedSchool,
    setSelectedSchool,
    clearSelectedSchool,
    schoolSelectorWarning,
    setSchoolSelectorWarning,
    defaultDistrict,
    defaultSchool,
    refetchDistricts,
    refetchSchools,
  ]);

  return (
    <RBACDataContext.Provider value={contextValue}>
      {children}
    </RBACDataContext.Provider>
  );
};